import { ArrowUpRight, Phone, Zap } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Severity = "Urgent" | "Common" | "Moderate" | "Minor";
const SEVERITY_COLOR: Record<Severity, string> = {
  Urgent: "#FF6B6B",
  Common: "#F5A623",
  Moderate: "#FFD166",
  Minor: "#7FE7B4"
};
const ISSUES: Array<{
  num: string;
  title: string;
  symptom: string;
  severity: Severity;
  fixTime: string;
}> = [{
  num: "01",
  title: "Motor Hums, Door Won't Move",
  symptom: "Power's there, motor's straining. Usually a snapped spring putting all the load on the opener, a stripped main gear, or a failed starting capacitor.",
  severity: "Urgent",
  fixTime: "~75 min"
}, {
  num: "02",
  title: "Completely Dead — No Sound, No Light",
  symptom: "Nothing happens when you press the remote or wall button. Tripped circuit, blown fuse, burnt control board, or end-of-life unit. Diagnosis tells us which.",
  severity: "Common",
  fixTime: "30–60 min"
}, {
  num: "03",
  title: "Door Reverses Mid-Travel",
  symptom: "Door starts to close then opens again, or vice versa. Safety sensors misaligned or blocked, force limits set wrong, or door binding from friction.",
  severity: "Common",
  fixTime: "~45 min"
}, {
  num: "04",
  title: "Loud Grinding or Screeching Sound",
  symptom: "Plastic main gear has stripped — extremely common on B&D and Merlin units after 8–10 years. Gear replacement is a quick, affordable repair.",
  severity: "Common",
  fixTime: "~60 min"
}, {
  num: "05",
  title: "Remote or Keypad Stopped Working",
  symptom: "Battery flat, wireless interference, programming lost, or receiver failure inside the head unit. Usually solved in 15 minutes with a re-pair or new remote.",
  severity: "Minor",
  fixTime: "~15 min"
}, {
  num: "06",
  title: "Safety Sensor LED Stuck Red",
  symptom: "One or both photo-eyes flashing red and the door won't close. Realignment, wire repair, dirty lens cleaning, or full sensor replacement.",
  severity: "Urgent",
  fixTime: "~30 min"
}, {
  num: "07",
  title: "Stops Halfway Up or Down",
  symptom: "Travel limits drifted out of spec, door balance off, or tracks binding. Limit reset + balance check + roller service usually solves it.",
  severity: "Moderate",
  fixTime: "~60 min"
}, {
  num: "08",
  title: "Slow, Sluggish or Weak Movement",
  symptom: "Opener sounds tired, door creeps up or down. Could be a fading capacitor, motor brushes worn, or an unbalanced door dragging extra weight onto the unit.",
  severity: "Minor",
  fixTime: "30–45 min"
}];
export default function MotorIssues() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="motor-issues-heading">
      {/* Blueprint grid */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: `
            linear-gradient(to right, ${GOLD} 1px, transparent 1px),
            linear-gradient(to bottom, ${GOLD} 1px, transparent 1px)
          `,
      backgroundSize: "80px 80px"
    }} />

      {/* Radial glow */}
      <div aria-hidden className="absolute -top-40 -left-40 w-[600px] h-[600px] pointer-events-none opacity-30" style={{
      background: `radial-gradient(circle, ${NAVY_LIGHT} 0%, transparent 60%)`
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}80 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-10 md:mb-14 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <Zap className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Motor Diagnostic Guide
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: "rgba(255,255,255,0.4)"
        }}>
            8 Most Common Symptoms · Sydney 2026
          </div>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 md:mb-16 items-end">
          <h2 id="motor-issues-heading" className="lg:col-span-8 font-extrabold text-white" style={{
          fontFamily: FONT_HEAD,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            What&rsquo;s your opener{" "}
            <span style={{
            color: GOLD
          }}>actually</span>
            <br className="hidden md:block" />
            doing right now?
          </h2>

          <p className="lg:col-span-4 text-[15px] md:text-base leading-relaxed" style={{
          color: "rgba(255,255,255,0.65)",
          fontFamily: FONT_BODY
        }}>
            Match the symptoms below and we&rsquo;ll diagnose by phone in 60
            seconds &mdash; then quote a fixed price before any tool comes out.
          </p>
        </div>

        {/* Gold separator with ticks */}
        <div aria-hidden className="relative h-px mb-12 md:mb-16" style={{
        background: `linear-gradient(90deg, ${GOLD}66 0%, ${GOLD}26 100%)`
      }}>
          {[0, 25, 50, 75, 100].map(p => <span key={p} className="absolute top-0 w-px h-2" style={{
          left: `${p}%`,
          backgroundColor: `${GOLD}80`
        }} />)}
        </div>

        {/* Issues grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-16 gap-y-0">
          {ISSUES.map((issue, idx) => <IssueRow key={issue.num} issue={issue} isLast={idx === ISSUES.length - 1} />)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 md:mt-20 relative overflow-hidden rounded-2xl border" style={{
        borderColor: `${GOLD}33`,
        background: `linear-gradient(135deg, ${NAVY_LIGHT}66 0%, ${NAVY} 100%)`
      }}>
          {/* Corner brackets */}
          <span aria-hidden className="absolute top-3 left-3 w-4 h-4 border-l border-t" style={{
          borderColor: GOLD
        }} />
          <span aria-hidden className="absolute top-3 right-3 w-4 h-4 border-r border-t" style={{
          borderColor: GOLD
        }} />
          <span aria-hidden className="absolute bottom-3 left-3 w-4 h-4 border-l border-b" style={{
          borderColor: GOLD
        }} />
          <span aria-hidden className="absolute bottom-3 right-3 w-4 h-4 border-r border-b" style={{
          borderColor: GOLD
        }} />

          <div className="relative p-6 md:p-10 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-2" style={{
              color: GOLD
            }}>
                Symptom Not Listed?
              </div>
              <h3 className="text-white font-bold" style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(1.375rem, 2.5vw, 1.875rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em"
            }}>
                Describe the sound, smell, or behaviour &mdash;
                <br className="hidden md:block" />
                we&rsquo;ve diagnosed it on the phone before.
              </h3>
            </div>

            <a href="tel:0485000933" className="group inline-flex items-center gap-3 rounded-full px-7 py-4 text-[15px] font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_BODY,
            boxShadow: `0 14px 40px -10px ${GOLD}99`
          }}>
              <Phone className="h-[18px] w-[18px]" strokeWidth={2.5} />
              Call 0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}80 50%, transparent 100%)`
    }} />
    </section>;
}
function IssueRow({
  issue
}: {
  issue: {
    num: string;
    title: string;
    symptom: string;
    severity: Severity;
    fixTime: string;
  };
  isLast: boolean;
}) {
  const sevColor = SEVERITY_COLOR[issue.severity];
  return <a href="tel:0485000933" className="group relative block py-7 md:py-8 transition-all duration-300" style={{
    borderTop: `1px solid rgba(255,255,255,0.08)`
  }} onMouseEnter={e => {
    const accent = e.currentTarget.querySelector("[data-accent]") as HTMLElement;
    if (accent) accent.style.width = "100%";
  }} onMouseLeave={e => {
    const accent = e.currentTarget.querySelector("[data-accent]") as HTMLElement;
    if (accent) accent.style.width = "0%";
  }}>
      {/* Hover gold underline */}
      <span data-accent aria-hidden className="absolute top-[-1px] left-0 h-px transition-all duration-500 ease-out" style={{
      width: "0%",
      background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}00 100%)`
    }} />

      <div className="flex items-start gap-5">
        {/* Oversize number */}
        <div className="flex-shrink-0 font-extrabold leading-none select-none transition-colors duration-300" style={{
        fontFamily: FONT_HEAD,
        color: `${GOLD}40`,
        fontSize: "clamp(2.5rem, 4.5vw, 3.75rem)",
        letterSpacing: "-0.04em",
        fontVariantNumeric: "tabular-nums",
        minWidth: "2.5ch"
      }}>
          {issue.num}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="font-bold text-white transition-colors duration-200 group-hover:text-[#F5A623]" style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(1.0625rem, 1.5vw, 1.25rem)",
            lineHeight: 1.25,
            letterSpacing: "-0.01em"
          }}>
              {issue.title}
            </h3>
            <ArrowUpRight className="flex-shrink-0 h-5 w-5 mt-0.5 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" strokeWidth={2} style={{
            color: GOLD
          } as React.CSSProperties} />
          </div>

          <p className="text-[14px] leading-[1.6] mb-3" style={{
          color: "rgba(255,255,255,0.62)"
        }}>
            {issue.symptom}
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.16em] uppercase" style={{
            color: sevColor
          }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: sevColor
            }} />
              {issue.severity}
            </span>

            <span className="text-[10px] font-semibold tracking-[0.16em] uppercase" style={{
            color: "rgba(255,255,255,0.35)"
          }}>
              Fix time · {issue.fixTime}
            </span>
          </div>
        </div>
      </div>
    </a>;
}